import React, { useEffect, useMemo, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { Box, Button } from "@mui/material";
import AppleIcon from '@mui/icons-material/Apple';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import AndroidLogo from '../../../images/android.png';
import eOpsLogo from '../../../images/eops.png';
import AppLoader from '../../../components/AppLoader/AppLoader';
import {
  companyShortName,
  convertDatewithTime,
  getLogo,
  openJsPdfInNewTab,
} from "../../../services/Functions";
import { resolveDiscoCompanyCodeForBranding } from "../../../utils/adminCompany";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import "jspdf-autotable";
import './MobileAppStats.scss';

const useSidebarTheme = () => {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute("data-sidebar-theme") || "light",
  );

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setTheme(root.getAttribute("data-sidebar-theme") || "light");

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["data-sidebar-theme"] });

    return () => observer.disconnect();
  }, []);

  return theme;
};

const chartThemeColors = (isDark) => ({
  text: isDark ? "#cbd5e1" : "#475569",
  grid: isDark ? "#334155" : "#eef2f6",
});

const logos = {
  10: { src: require('../../../images/discologos/pitc11-removebg-preview.png'), name: "PITC" },
  11: { src: require('../../../images/discologos/lesco.jpg'), name: "LESCO" },
  12: { src: require('../../../images/discologos/gepco.jpg'), name: "GEPCO" },
  13: { src: require('../../../images/discologos/fesco.jpg'), name: "FESCO" },
  14: { src: require('../../../images/discologos/iesco logo.png'), name: "IESCO" },
  15: { src: require('../../../images/discologos/mepco1.jpg'), name: "MEPCO" },
  26: { src: require('../../../images/discologos/pesco1.jpg'), name: "PESCO" },
  27: { src: require('../../../images/discologos/hazeco_logo-removebg-preview.png'), name: "HAZECO" },
  37: { src: require('../../../images/discologos/hesco logo.png'), name: "HESCO" },
  38: { src: require('../../../images/discologos/sepco.jpg'), name: "SEPCO" },
  48: { src: require('../../../images/discologos/qesco.png'), name: "QESCO" },
  59: { src: require('../../../images/discologos/tesco.jpg'), name: "TESCO" },
  91: { src: require('../../../images/discologos/sundar-logo.png'), name: "SUNDAR" },
};

const formatCount = (value) => Number(value || 0).toLocaleString();

const COMPANY_NAMES = {
  '10': 'Power Information Technology Company',
  '11': 'Lahore Electric Supply Company',
  '12': 'Gujranwala Electric Power Company',
  '13': 'Faisalabad Electric Supply Company',
  '14': 'Islamabad Electric Supply Company',
  '15': 'Multan Electric Power Company',
  '26': 'Peshawar Electric Supply Company',
  '27': 'Hazara Electric Supply Company',
  '37': 'Hyderabad Electric Supply Company',
  '38': 'Sukkur Electric Power Company',
  '48': 'Quetta Electric Supply Company',
  '59': 'Tribal Electric Supply Company',
};

const getCompanyName = (code) => COMPANY_NAMES[String(code)] || 'Electric Supply Company';

const MobileAppStats = () => {
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const sidebarTheme = useSidebarTheme();
  const isDark = sidebarTheme === "dark";
  const { text: chartText, grid: gridColor } = chartThemeColors(isDark);
  const {
    code: userCode,
    roleId,
    selectedAdminCompanyCode,
    name: userName,
    roleName,
  } = useSelector((state) => state.auth);
  const companyCode = String(
    resolveDiscoCompanyCodeForBranding(userCode, roleId, selectedAdminCompanyCode) || userCode || '',
  );

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
          discoCode: '10',
        });

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        };

        const response = await fetch(
          "https://api-powersmart.pitc.com.pk/appUser/getRegisteredAppUsers",
          requestOptions
        );

        const result = await response.json();

        if (result?.status === "200" && result?.data?.length > 0) {
          const enhancedData = result.data.map(item => ({
            ...item,
            logo: logos[item.discoCode]?.src || logos[10].src,
            companyName: logos[item.discoCode]?.name || companyShortName(item.discoCode.toString()),
          }));
          setStats(enhancedData);
        } else {
          setStats([]);
        }
      } catch (error) {
        setStats([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const disco10 = stats.find(item => item.discoCode === "10");
  const otherDiscos = stats.filter(item => item.discoCode !== "10");

  const handleExportPdf = () => {
    if (!stats.length || exportingPdf) return;

    setExportingPdf(true);
    try {
      const pdf = new jsPDF("portrait", "mm", "a4", { compressPdf: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 14;
      const tallLogoCodes = ["11", "12", "13", "16", "27", "48", "59"];
      const isTallLogo = tallLogoCodes.includes(companyCode);
      const logoWidth = isTallLogo ? 22 : 28;
      const logoHeight = isTallLogo ? 18 : 9;

      try {
        pdf.addImage(getLogo(companyCode), "PNG", marginX, 8, logoWidth, logoHeight);
        pdf.addImage(
          eOpsLogo,
          "PNG",
          isTallLogo ? marginX + 24 : marginX + 1,
          isTallLogo ? 14 : 19,
          18,
          7,
        );
      } catch (_) {
        // Logos are optional.
      }

      pdf.setFontSize(9);
      pdf.setTextColor(30, 41, 59);
      pdf.text(convertDatewithTime(new Date()), pageWidth - 14, 12, { align: "right" });
      pdf.text(`Name: ${userName || "-"}`, pageWidth - 14, 17, { align: "right" });
      pdf.text(`Designation: ${roleName || "-"}`, pageWidth - 14, 22, { align: "right" });

      pdf.setFontSize(12);
      pdf.setTextColor(9, 102, 157);
      pdf.text(getCompanyName(companyCode), pageWidth / 2, 12, { align: "center" });
      pdf.setFontSize(11);
      pdf.setTextColor(15, 23, 42);
      pdf.text("Power Smart Mobile App", pageWidth / 2, 18, { align: "center" });
      pdf.setFontSize(9);
      pdf.setTextColor(71, 85, 105);
      pdf.text("Registered app users by platform and DISCO", pageWidth / 2, 24, {
        align: "center",
      });

      const summaryAndroid = disco10?.androidUsers || 0;
      const summaryIos = disco10?.iosUsers || 0;
      const summaryTotal = disco10?.totalUsers || 0;

      pdf.autoTable({
        startY: 28,
        head: [["Android Users", "iOS Users", "Total Users"]],
        body: [[
          formatCount(summaryAndroid),
          formatCount(summaryIos),
          formatCount(summaryTotal),
        ]],
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 3,
          halign: "center",
          valign: "middle",
          textColor: [30, 41, 59],
          lineColor: [226, 232, 240],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: [9, 102, 157],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 9,
        },
        bodyStyles: {
          fillColor: [248, 250, 252],
          fontStyle: "bold",
          fontSize: 11,
        },
        margin: { left: marginX, right: marginX },
      });

      const tableBody = otherDiscos.map((item, index) => [
        String(index + 1),
        item.companyName || "-",
        formatCount(item.androidUsers),
        formatCount(item.iosUsers),
        formatCount(item.totalUsers),
      ]);

      if (disco10) {
        tableBody.push([
          "",
          "Grand Total",
          formatCount(disco10.androidUsers),
          formatCount(disco10.iosUsers),
          formatCount(disco10.totalUsers),
        ]);
      }

      pdf.autoTable({
        startY: (pdf.lastAutoTable?.finalY || 42) + 6,
        head: [["Sr", "DISCO", "Android", "iOS", "Total"]],
        body: tableBody,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2.2,
          halign: "center",
          valign: "middle",
          textColor: [30, 41, 59],
          lineColor: [226, 232, 240],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: [9, 102, 157],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 9,
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        columnStyles: {
          0: { cellWidth: 14 },
          1: { cellWidth: 45, halign: "left" },
          2: { cellWidth: 35 },
          3: { cellWidth: 35 },
          4: { cellWidth: "auto", fontStyle: "bold" },
        },
        margin: { left: marginX, right: marginX, bottom: 14 },
        didParseCell: (data) => {
          if (data.section !== "body") return;
          const label = String(data.row.raw?.[1] || "");
          if (label === "Grand Total") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [224, 242, 254];
            data.cell.styles.textColor = [9, 102, 157];
          }
        },
      });

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, {
          align: "right",
        });
      }

      openJsPdfInNewTab(
        pdf,
        `Power-Smart-Mobile-App-Stats-${convertDatewithTime(new Date()).replace(/[^\w.-]+/g, "-")}.pdf`,
      );
    } finally {
      setExportingPdf(false);
    }
  };

  const pieChart = useMemo(
    () =>
      disco10
        ? {
            chart: { type: "pie", height: 180, backgroundColor: "transparent", spacing: [4, 4, 4, 4] },
            title: { text: "" },
            tooltip: { pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)" },
            plotOptions: {
              pie: {
                allowPointSelect: false,
                cursor: "pointer",
                size: "85%",
                innerSize: "52%",
                dataLabels: {
                  enabled: true,
                  distance: 12,
                  format: "{point.percentage:.1f}%",
                  style: { fontSize: "10px", fontWeight: "500", color: chartText, textOutline: "none" },
                },
              },
            },
            credits: { enabled: false },
            series: [
              {
                name: "Registered Users",
                colorByPoint: true,
                data: [
                  { name: "Android", y: disco10.androidUsers || 0, color: "#16a34a" },
                  { name: "iOS", y: disco10.iosUsers || 0, color: isDark ? "#94a3b8" : "#64748b" },
                ],
              },
            ],
          }
        : null,
    [disco10, chartText, isDark],
  );

  const comparisonChart = useMemo(
    () => ({
      chart: { type: "column", height: 220, backgroundColor: "transparent", spacing: [8, 8, 8, 8] },
      title: { text: "" },
      xAxis: {
        categories: otherDiscos.map((item) => item.companyName),
        crosshair: true,
        labels: { style: { fontSize: "10px", color: chartText } },
        lineColor: gridColor,
        tickColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: { text: null },
        labels: {
          formatter: function () {
            return (this.value / 1000000).toFixed(2) + "M";
          },
          style: { fontSize: "10px", color: chartText },
        },
        gridLineColor: gridColor,
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          return this.points
            .map(
              (point) =>
                `<b>${point.series.name}:</b> ${Number(point.y || 0).toLocaleString()}<br/>`,
            )
            .join("");
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.12,
          groupPadding: 0.08,
          borderWidth: 0,
          borderRadius: 2,
        },
      },
      credits: { enabled: false },
      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        itemStyle: { fontSize: "10px", color: chartText, fontWeight: "400" },
        symbolRadius: 2,
      },
      series: [
        {
          name: "Android",
          data: otherDiscos.map((item) => item.androidUsers || 0),
          color: "#16a34a",
        },
        {
          name: "iOS",
          data: otherDiscos.map((item) => item.iosUsers || 0),
          color: isDark ? "#94a3b8" : "#64748b",
        },
        {
          name: "Total",
          data: otherDiscos.map((item) => item.totalUsers || 0),
          color: "#2563eb",
        },
      ],
    }),
    [otherDiscos, chartText, gridColor, isDark],
  );

  return (
    <Box className="form-box">
      <div className="mobile-app-stats">
        <div className="mobile-app-stats__page-header">
          <img src={logos[10].src} alt="PITC Logo" />
          <div className="mobile-app-stats__page-header-copy">
            <h1>Power Smart Mobile App</h1>
            <p>Registered app users by platform and DISCO</p>
          </div>
          <Button
            className="mobile-app-stats__export-btn"
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={handleExportPdf}
            disabled={isLoading || !stats.length || exportingPdf}
          >
            {exportingPdf ? "Exporting…" : "Export PDF"}
          </Button>
        </div>

        {isLoading ? (
          <div className="mobile-app-stats__loading">
            <AppLoader size={40} />
          </div>
        ) : stats.length > 0 ? (
          <>
            <div className="mobile-app-stats__kpi-row">
              {disco10 && (
                <>
                  <div className="mobile-app-stats-kpi mobile-app-stats-kpi--android">
                    <span className="mobile-app-stats-kpi__icon">
                      <img src={AndroidLogo} alt="" />
                    </span>
                    <div>
                      <span className="mobile-app-stats-kpi__label">Android</span>
                      <span className="mobile-app-stats-kpi__value">{formatCount(disco10.androidUsers)}</span>
                    </div>
                  </div>
                  <div className="mobile-app-stats-kpi mobile-app-stats-kpi--ios">
                    <span className="mobile-app-stats-kpi__icon">
                      <AppleIcon />
                    </span>
                    <div>
                      <span className="mobile-app-stats-kpi__label">iOS</span>
                      <span className="mobile-app-stats-kpi__value">{formatCount(disco10.iosUsers)}</span>
                    </div>
                  </div>
                  <div className="mobile-app-stats-kpi mobile-app-stats-kpi--total">
                    <span className="mobile-app-stats-kpi__icon">
                      <PeopleAltIcon />
                    </span>
                    <div>
                      <span className="mobile-app-stats-kpi__label">Total Users</span>
                      <span className="mobile-app-stats-kpi__value">{formatCount(disco10.totalUsers)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="mobile-app-stats__summary-row">
              {disco10 && pieChart && (
                <div className="mobile-app-stats-panel mobile-app-stats-panel--platform">
                  <div className="mobile-app-stats-panel__title">Platform Split</div>
                  <div className="mobile-app-stats-panel__body mobile-app-stats-panel__body--pie">
                    <HighchartsReact highcharts={Highcharts} options={pieChart} />
                  </div>
                </div>
              )}

              <div className="mobile-app-stats-panel mobile-app-stats-panel--wide mobile-app-stats-panel--comparison">
                <div className="mobile-app-stats-panel__title">DISCO Comparison</div>
                <div className="mobile-app-stats-panel__body">
                  <HighchartsReact highcharts={Highcharts} options={comparisonChart} />
                </div>
              </div>
            </div>

            <div className="mobile-app-stats-panel mobile-app-stats-panel--breakdown">
              <div className="mobile-app-stats-panel__title">DISCO Breakdown</div>
              <div className="mobile-app-stats-table-wrap">
                <table className="mobile-app-stats-table">
                  <thead>
                    <tr>
                      <th>DISCO</th>
                      <th>Android</th>
                      <th>iOS</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherDiscos.map((item) => (
                      <tr key={item.discoCode}>
                        <td>
                          <span className="mobile-app-stats-table__disco">
                            <img src={item.logo} alt="" />
                            <span>{item.companyName}</span>
                          </span>
                        </td>
                        <td>{formatCount(item.androidUsers)}</td>
                        <td>{formatCount(item.iosUsers)}</td>
                        <td className="mobile-app-stats-table__total">{formatCount(item.totalUsers)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="mobile-app-stats__empty">No Data Found.</div>
        )}
      </div>
    </Box>
  );
};

export default MobileAppStats;
